import { Controller } from "@hotwired/stimulus";

/*
 * Pins a model number into the search form, filtering results to it.
 *
 * The controller sits on a card section. On connect, it reads the
 * corresponding search input to determine its initial state — so the
 * icon is correct after a Turbo Frame reload too (the inputs live
 * outside the frame and keep their values).
 *
 * Clicking the pin button sets the input's value (or clears it to
 * unpin), syncs any partner inputs (mobile/desktop duplicates), and
 * fires an input event so the form's debounced submit kicks in.
 * click.stop on the button prevents the click from bubbling up to
 * the card's own select handler.
 *
 * Values:
 *   field (String) — search form field name, e.g. "outdoor_unit_model_number_cont"
 *   model (String) — the model number this section represents
 *
 * Targets:
 *   off — icon shown when not pinned (outline)
 *   on  — icon shown when pinned (solid)
 *
 * Usage:
 *
 *   <div data-controller="pin"
 *        data-pin-field-value="outdoor_unit_model_number_cont"
 *        data-pin-model-value="BOVA-36HDN1-M20G"
 *        class="relative group">
 *
 *     <!-- section content -->
 *
 *     <button type="button"
 *             data-action="click.stop->pin#toggle"
 *             class="absolute bottom-2 right-2">
 *       <%= inline_svg_tag "icons/check-circle-outline.svg",
 *             data: { pin_target: "off" } %>
 *       <%= inline_svg_tag "icons/check-circle-solid.svg",
 *             class: "hidden",
 *             data: { pin_target: "on" } %>
 *     </button>
 *   </div>
 */

export default class extends Controller {
  static targets = ["off", "on", "button"];
  static values = { field: String, model: String };

  connect() {
    this.refresh();
  }

  toggle(event) {
    event.stopPropagation();

    const input = this.findInput();
    if (!input) return;

    const newValue = this.pinned ? "" : this.modelValue;
    document.querySelectorAll(`input[name="${input.name}"]`).forEach((i) => {
      i.value = newValue;
    });

    input.dispatchEvent(new Event("input", { bubbles: true }));
    this.refresh();
  }

  refresh() {
    const pinned = this.pinned;
    this.offTarget.classList.toggle("hidden", pinned);
    this.onTarget.classList.toggle("hidden", !pinned);
    if (this.hasButtonTarget) {
      this.buttonTarget.classList.toggle("opacity-0", !pinned);
    }
  }

  get pinned() {
    const input = this.findInput();
    return !!input && input.value === this.modelValue;
  }

  findInput() {
    return document.querySelector(`input[name="q[${this.fieldValue}]"]`);
  }
}
